import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import RequireAuth from '../components/guards/RequireAuth';
import RedirectIfAuthenticated from '../components/guards/RedirectIfAuthenticated';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(false);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    let retryTimeout;

    const setupAuthListener = () => {
      return onAuthStateChanged(
        auth,
        async (user) => {
          if (user) {
            try {
              // Get user data from Firestore
              const userDoc = await getDoc(doc(db, 'users', user.uid));
              const userData = userDoc.data();
              setIsOnboarded(userData?.hasCompletedOnboarding || false);
            } catch (error) {
              console.error('Error fetching user data:', error);
              setIsOnboarded(false);
            }
          } else {
            setIsOnboarded(false);
          }
          setUser(user);
          setLoading(false);
          setError(null);
          retryCount = 0; // Reset retry count on successful connection
        },
        (error) => {
          console.error('Auth state change error:', error);
          setError(error);
          
          // Retry logic for connection errors
          if (retryCount < maxRetries && 
              (error.code === 'auth/network-request-failed' || 
               error.message.includes('ERR_CONNECTION_CLOSED'))) {
            retryCount++;
            const delay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Exponential backoff
            console.log(`Retrying auth connection in ${delay}ms (attempt ${retryCount})`);
            retryTimeout = setTimeout(setupAuthListener, delay);
          } else {
            setLoading(false);
          }
        }
      );
    };

    const unsubscribe = setupAuthListener();

    return () => {
      unsubscribe();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsOnboarded(false);
      return true;
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isOnboarded,
    setIsOnboarded,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export { RequireAuth, RedirectIfAuthenticated }; 